import type { TenantWithOwner } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import { usePartnerTenants } from "@/lib/hooks/use-admin";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import { useT } from "@/lib/i18n/use-t";
import { TenantDialog } from "@/pages/tenants/tenant-dialog";
import { TenantsTable } from "@/pages/tenants/tenants-table";
import { T } from "./partner-tenants-wrapper.i18n";

const PAGE_SIZE = 20;

export function PartnerTenantsWrapper() {
  const t = useT(T);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<TenantWithOwner | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebouncedValue(searchQuery);
  const [page, setPage] = useState(1);

  // QL-136 — search and paging run on the server.
  const { tenants, pagination, isLoading, isFetching, mutate } = usePartnerTenants({
    search: debouncedSearch,
    page,
    pageSize: PAGE_SIZE,
  });

  // A new search starts from the first page.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // A page can empty out after a delete on the last page; step back to the
  // last page that still exists so the pager stays reachable.
  useEffect(() => {
    if (page > pagination.totalPages) setPage(pagination.totalPages);
  }, [page, pagination.totalPages]);

  const emptyMessage = debouncedSearch.trim() ? t.noSearchResults : undefined;

  const handleCreate = () => {
    setEditingTenant(null);
    setDialogOpen(true);
  };

  const handleEdit = (tenant: TenantWithOwner) => {
    setEditingTenant(tenant);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingTenant(null);
  };

  return (
    <>
      <div className="flex items-center justify-between gap-4 mb-4">
        <SearchInput
          className="flex-1 max-w-sm"
          placeholder={t.searchPlaceholder}
          value={searchQuery}
          onValueChange={setSearchQuery}
        />
        <Button onClick={handleCreate}>{t.addTenant}</Button>
      </div>

      <TenantsTable
        tenants={tenants}
        onEdit={handleEdit}
        canDelete={false}
        mutate={mutate}
        emptyMessage={emptyMessage}
        page={pagination.page}
        pageSize={pagination.pageSize}
        totalCount={pagination.totalCount}
        totalPages={pagination.totalPages}
        onPageChange={setPage}
        isLoading={isLoading}
        isFetching={isFetching}
      />

      <TenantDialog
        open={dialogOpen}
        tenant={editingTenant}
        onClose={handleDialogClose}
        mutate={mutate}
      />
    </>
  );
}
